import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Table } from "@mantine/core"
import { useDebouncedState } from "@mantine/hooks";

import { EmptyMessage, Loader, Pagination } from "components";
import { AuthorityConstants, ServiceConstants } from "constant";
import { useListHook } from "hooks";
import { CardResponse } from "types";
import { AppUtils, UserUtils } from "utils";

import { IRootState } from "config/redux";
import CardListItem from "./item";
import { tableClasses } from "styles";

const { ROLE_CR_R } = AuthorityConstants;
const { REQUEST_PENDING, REQUEST_CARDS_LIST_ID } = ServiceConstants;

interface CardListProps {
  fetch: (page: number, search: string | undefined) => void;
}

const CardList = ({ fetch }: CardListProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CARDS_LIST_ID]);
  const { page, content, updatePageable, resetList, totalPages, refreshList, setPage } = useListHook();
  const [search, ] = useDebouncedState('', 500);

  useEffect(() => {
    resetList();
  }, []);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_CARDS_LIST_ID, updatePageable);
  }, [serviceObject]);

  useEffect(() => {
    if (page === undefined) {
      return;
    }
    if (page === -1) {
      resetList();
      return;
    }
    fetch(page, search);
  }, [page]);

  useEffect(() => {
    if (search === undefined) {
      return;
    }
    refreshList();
  }, [search]);

  const isLoading = useCallback(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject]);

  const rows = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((el: CardResponse) => <CardListItem key={el.id} item={el} t={t} />)
  }, [content])

  return (
    <Box pos={'relative'}>
      <Loader overlay={true} visible={content === undefined || isLoading()} />
      {content && (
        content.length === 0 ?
          <EmptyMessage message={t('card.list.empty')} />
        :
        <>
          <Table className={tableClasses.table}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('user.fullName.label')}</Table.Th>
                <Table.Th visibleFrom={'md'}>{t('user.gender.label')}</Table.Th>
                <Table.Th>{t('user.phone.label')}</Table.Th>
                <Table.Th visibleFrom={'md'}>{t('user.address.label')}</Table.Th>
                <Table.Th>{t('source.title')}</Table.Th>
                <Table.Th>{t('card.requested.label')}</Table.Th>
                <Table.Th ta={'center'}>{t('card.comment.title')}</Table.Th>
                {UserUtils.hasAuthority(ROLE_CR_R) && <Table.Th ta={'center'}>{t('link.title')}</Table.Th>}
                <Table.Th visibleFrom={'md'} ta={'center'}>{t('card.crm.label')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
          </Table>
          <Pagination value={page} onChange={setPage} total={totalPages} />
        </>
      )}
    </Box>
  )
}

export default CardList;